import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, IconButton, Tooltip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { useUserEngagementComparisonQuery } from 'api';
import { Numeric, PaddedPaper } from 'components';
import { useCurrentUser, useNotify } from 'hooks';

import { Loader, Rank, RankChart } from './Summary';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

const useStyles = makeStyles({
  tooltipButton: {
    padding: 0,
  },
});

const NetworkComparison: FC = () => {
  const classes = useStyles();
  const currentUser = useCurrentUser();
  const notify = useNotify();
  const { t } = useTranslation();

  const { data, isLoading, error } = useUserEngagementComparisonQuery(
    {
      user_id: currentUser.id,
    },
    {
      onError: notify.queryError,
    }
  );

  function renderNetworkSize() {
    const { reach_size_user, reach_size_client_min, reach_size_client_max, reach_size_user_rank } =
      data.userEngagementComparison;

    const scale = reach_size_user / (reach_size_client_max - reach_size_client_min);
    const scalePercentage = `${(scale * 100).toFixed(2)}%`;

    return (
      <Fragment>
        <Grid container alignItems="center" spacing={1}>
          <Grid item>
            <Typography variant="subtitle2">{t('network:networkSize')}</Typography>
          </Grid>
          <Grid item>
            <Tooltip title={t('network:networkSizeTooltip')}>
              <IconButton className={classes.tooltipButton}>
                <InfoOutlinedIcon fontSize="small" color="disabled" />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
        <Grid container alignItems="center" spacing={1}>
          <Grid item>
            <Rank rank={reach_size_user_rank} />
          </Grid>
        </Grid>
        <Box marginTop={4} marginBottom={2}>
          <Grid container alignItems="center" spacing={1}>
            <Grid item>
              <Typography variant="subtitle2" color="textSecondary">
                <Numeric value={reach_size_client_min} />
              </Typography>
            </Grid>
            <Grid item xs>
              <RankChart
                scale={scale}
                valueNode={<Numeric value={reach_size_user} />}
                percentage={scalePercentage}
              />
            </Grid>
            <Grid item>
              <Typography variant="subtitle2" color="textSecondary">
                <Numeric value={reach_size_client_max} />
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Fragment>
    );
  }

  function renderEngagementsPerShare() {
    const {
      engagements_per_share_user,
      engagements_per_share_client_min,
      engagements_per_share_client_max,
      engagements_per_share_user_rank,
    } = data.userEngagementComparison;

    const scale =
      engagements_per_share_user /
      (engagements_per_share_client_max - engagements_per_share_client_min);
    const scalePercentage = `${(scale * 100).toFixed(2)}%`;

    return (
      <Fragment>
        <Grid container alignItems="center" spacing={1}>
          <Grid item>
            <Typography variant="subtitle2">{t('network:engagementsPerShare')}</Typography>
          </Grid>
          <Grid item>
            <Tooltip title={t('network:engagementsPerShareTooltip')}>
              <IconButton className={classes.tooltipButton}>
                <InfoOutlinedIcon fontSize="small" color="disabled" />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
        <Grid container alignItems="center" spacing={1}>
          <Grid item>
            <Rank rank={engagements_per_share_user_rank} />
          </Grid>
        </Grid>
        <Box marginTop={4} marginBottom={2}>
          <Grid container alignItems="center" spacing={1}>
            <Grid item>
              <Typography variant="subtitle2" color="textSecondary">
                <Numeric decimals={1} value={engagements_per_share_client_min} />
              </Typography>
            </Grid>
            <Grid item xs>
              <RankChart
                scale={scale}
                valueNode={<Numeric value={engagements_per_share_user} decimals={1} />}
                percentage={scalePercentage}
              />
            </Grid>
            <Grid item>
              <Typography variant="subtitle2" color="textSecondary">
                <Numeric decimals={1} value={engagements_per_share_client_max} />
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Fragment>
    );
  }

  function renderNetworkGrowth() {
    const {
      reach_growth_user,
      reach_growth_client_min,
      reach_growth_client_max,
      reach_growth_user_rank,
    } = data.userEngagementComparison;

    const scale = reach_growth_user / (reach_growth_client_max - reach_growth_client_min);
    const scalePercentage = `${(scale * 100).toFixed(2)}%`;

    return (
      <Fragment>
        <Grid container alignItems="center" spacing={1}>
          <Grid item>
            <Typography variant="subtitle2">{t('network:networkGrowth')}</Typography>
          </Grid>
          <Grid item>
            <Tooltip title={t('network:networkGrowthTooltip')}>
              <IconButton className={classes.tooltipButton}>
                <InfoOutlinedIcon fontSize="small" color="disabled" />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
        <Grid container alignItems="center" spacing={1}>
          <Grid item>
            <Rank rank={reach_growth_user_rank} />
          </Grid>
        </Grid>
        <Box marginTop={4} marginBottom={2}>
          <Grid container alignItems="center" spacing={1}>
            <Grid item>
              <Typography variant="subtitle2" color="textSecondary">
                <Numeric value={reach_growth_client_min} format="0.0%" />
              </Typography>
            </Grid>
            <Grid item xs>
              <RankChart
                scale={scale}
                valueNode={<Numeric value={reach_growth_user} format="0.0%" />}
                percentage={scalePercentage}
              />
            </Grid>
            <Grid item>
              <Typography variant="subtitle2" color="textSecondary">
                <Numeric value={reach_growth_client_max} format="0.0%" />
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Fragment>
    );
  }

  if (isLoading || error) {
    return (
      <PaddedPaper>
        <Grid container spacing={4}>
          <Grid item xs={4}>
            <Loader />
          </Grid>
          <Grid item xs={4}>
            <Loader />
          </Grid>
          <Grid item xs={4}>
            <Loader />
          </Grid>
        </Grid>
      </PaddedPaper>
    );
  }

  return (
    <PaddedPaper>
      <Grid container spacing={4}>
        <Grid item xs={4}>
          {renderNetworkSize()}
        </Grid>
        <Grid item xs={4}>
          {renderEngagementsPerShare()}
        </Grid>
        <Grid item xs={4}>
          {renderNetworkGrowth()}
        </Grid>
      </Grid>
    </PaddedPaper>
  );
};

export default NetworkComparison;
